import { useState, useMemo, useEffect, useCallback } from 'react';
import { useConnectionStore } from '@/store/connectionStore';
import { DeviceType, resolveDeviceType } from '@packages/types/device/common/deviceTypes';
import type { DateRange } from '@/components/ui';
import type { ErrorMessage } from '@/utils/errors/types';
import { parseDatalogApiError } from '@/utils/errors';

interface LogTabDevice {
  serial_number: string;
  device_type?: string;
}

interface UseLogTabStateOptions {
  device: LogTabDevice;
  defaultRangeDays?: number;
}

/**
 * 사이트 로그 탭에서 반복되는 상태를 통합하는 훅.
 * - isDownloading 구독
 * - loading / error 상태
 * - dateRange (기본 1달)
 * - resolvedType / isLite / isMini
 * - isDownloading일 때 fetch를 스킵하는 shouldFetch 플래그
 */
export function useLogTabState({ device, defaultRangeDays = 30 }: UseLogTabStateOptions) {
  const isDownloading = useConnectionStore((state: any) => state.isDownloading);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ErrorMessage | null>(null);

  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const today = new Date();
    const past = new Date(today);
    past.setDate(today.getDate() - defaultRangeDays);
    return {
      from: past.toISOString().split('T')[0],
      to: today.toISOString().split('T')[0],
    };
  });

  const resolvedType = useMemo(
    () => resolveDeviceType(device.device_type, device.serial_number),
    [device.device_type, device.serial_number],
  );
  const isLite = resolvedType === DeviceType.LITE;
  const isMini = resolvedType === DeviceType.MINI;

  const shouldFetch = !isDownloading && !!dateRange.from && !!dateRange.to;

  const isMultiDayRange = useMemo(() => {
    if (!dateRange.from || !dateRange.to) return false;
    return dateRange.from !== dateRange.to;
  }, [dateRange.from, dateRange.to]);

  const handleError = useCallback((err: unknown) => {
    setError(parseDatalogApiError(err));
  }, []);

  return {
    isDownloading,
    loading,
    setLoading,
    error,
    setError,
    handleError,
    dateRange,
    setDateRange,
    resolvedType,
    isLite,
    isMini,
    shouldFetch,
    isMultiDayRange,
  };
}

interface UseLogFetchOptions {
  shouldFetch: boolean;
  deps: unknown[];
  fetchFn: () => Promise<void>;
}

/**
 * isDownloading일 때 자동으로 스킵하고, deps 변경 시 fetchFn을 호출하는 훅.
 */
export function useLogFetch({ shouldFetch, deps, fetchFn }: UseLogFetchOptions) {
  useEffect(() => {
    if (!shouldFetch) return;
    fetchFn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetch, ...deps]);
}
